import config from '@config';

import type {
	ObservabilityEvent,
	ObservabilityEventContext,
	ObservabilityEventInput,
} from './observability.events';

declare const __TEMPLATE__: string;
declare const __SW_VERSION__: string | undefined;

/**
 * A reporter is anywhere an observability event can be sent. Deliberately a
 * plain interface, not a class -- `reporters/local-reporter.ts` is the only
 * implementation today; a future remote reporter is a new file here plus one
 * `registerObservabilityReporter` call, with zero changes to any call site
 * that uses `report()`.
 *
 * This pipeline is structurally parallel to `src/shared/events/` and
 * `src/shared/analytics/`'s destinations but deliberately independent of
 * them: analytics is tenant-owned and consent-gated (it flows into the publisher's own GTM/GA4/etc, and whether
 * it fires depends on the publisher's config and the reader's consent
 * choice); observability answers "is the platform's own code broken," which
 * must not depend on either. That's only defensible because every event
 * here is aggregate-only by construction -- no user identifiers, no
 * response bodies, no raw tokens (see `observability.events.ts`). Relaxing
 * that constraint anywhere would need the same consent machinery analytics
 * has, and should not be done without revisiting this file's header.
 */
export interface ObservabilityReporter {
	name: string;
	report(event: ObservabilityEvent): void;
}

const reporters = new Set<ObservabilityReporter>();

/** Returns an unregister function, mirroring `useEffect` cleanup ergonomics. */
export function registerObservabilityReporter(
	reporter: ObservabilityReporter,
): () => void {
	reporters.add(reporter);
	return () => {
		reporters.delete(reporter);
	};
}

function buildContext(): ObservabilityEventContext {
	return {
		appKey: config.build.appKey,
		template: __TEMPLATE__,
		version: config.build.version,
		buildNumber: config.build.buildNumber,
		swVersion:
			typeof __SW_VERSION__ !== 'undefined' ? __SW_VERSION__ : 'dev',
		timestamp: Date.now(),
	};
}

/**
 * The single call site every observability producer uses. Fans out to every
 * registered reporter; a reporter throwing must never propagate back to the
 * caller -- reporting an error must not be able to cause one.
 */
export function report(event: ObservabilityEventInput): void {
	const fullEvent = { ...event, ...buildContext() } as ObservabilityEvent;

	for (const reporter of reporters) {
		try {
			reporter.report(fullEvent);
		} catch {
			// Swallowed deliberately -- see the module comment above `report`.
		}
	}
}
